import { ChangeDetectionStrategy, Component } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { candidateLabelMapping, TranslationKey } from "@ankaadia/ankaadia-shared";
import { NewUiButton, translateSafely } from "@ankaadia/ankaadia-shared-frontend";
import { FieldType, FormlyFieldConfig, FormlyFormOptions, FormlyModule } from "@ngx-formly/core";
import { cloneDeep, isFunction, isNil, isString, last, omit, toPath } from "lodash";
import { Card } from "primeng/card";
import { NewUiCandidateListMultiEditInlineComponent } from "../../features/candidates/candidate-list-multi-edit/new-ui-candidate-list-multi-edit-inline.component";
import { TranslateDirective } from "../../shared/transloco/translate.directive";

interface RowData {
  model: Record<string, unknown>;
  fields: FormlyFieldConfig[];
  options: FormlyFormOptions;
}

/**
 * Renders a multi-editable formly list (AB#21656, `os.profile.qualifications`/`furtherEducations`/`experiences`/
 * `languageSkills`/`vaccinations`) using the same row mask ("fieldArray") the single-candidate view uses, hosted
 * inline via {@link NewUiCandidateListMultiEditInlineComponent} instead of formly's own `repeat-tabs` array
 * handling. Installed in place of `repeat-tabs` by {@link NewUiCandidateProfileComponent.handleMultiEditLists}.
 *
 * Each row gets its own clone of the original `fieldArray.fieldGroup` (stashed on `props.rowField` by the patch
 * pass) and its own plain model object, built once in {@link createElementForm} and cached by the row's `FormGroup`
 * instance (kept stable across summary reloads by the inline host). The nested `formly-form` shares this field's
 * `formState` so expressions/hooks that read `formState.profession`/`immigrationCountry`/etc. keep working; ones
 * that need `formState.candidateId` (document upload/date controls) no-op, same as in the EMI act.
 */
@Component({
  selector: "new-ui-formly-candidate-list-multi-edit",
  templateUrl: "./new-ui-formly-candidate-list-multi-edit.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, NewUiCandidateListMultiEditInlineComponent, Card, NewUiButton, TranslateDirective],
})
export class NewUiFormlyCandidateListMultiEditComponent extends FieldType {
  private readonly rowData = new WeakMap<FormGroup, RowData>();

  /** Builds an (empty) row `FormGroup`; the nested `formly-form` populates it from the row's own fields/model. */
  protected readonly createElementForm = (element: Record<string, unknown> | null): FormGroup => {
    const form = new FormGroup({});
    const defaultValue = this.props.defaultValue as (() => Record<string, unknown>) | undefined;
    const model = element ?? defaultValue?.() ?? {};
    // the whole fieldArray definition becomes one keyless group so that its own props (e.g. the grid class in
    // `editFieldGroupClassName`) and wrappers apply to the row exactly like in the accordion rendering
    const rowField = this.props.rowField ? omit(cloneDeep(this.props.rowField), "key") : undefined;
    const fields = rowField ? [rowField] : [];
    this.rowData.set(form, { model, fields, options: { formState: this.options.formState } });
    return form;
  };

  /** The list's profile label (e.g. "Qualifikationen"), looked up by the last segment of `os.profile.<list>`. */
  protected get sectionLabel(): string {
    const listKey = last(toPath(this.props.listPath));
    // Plain index access on purpose: lodash `get` on this mapping, contextually typed by the ~12k-member
    // `TranslationKey` union, costs ~25s and ~1.4GB in every type-check (tsc, tsserver, Angular LS, ESLint).
    const labelsByListKey: Record<string, TranslationKey> = candidateLabelMapping;
    const labelKey = labelsByListKey[listKey];
    return isNil(labelKey) ? listKey : translateSafely(labelKey);
  }

  protected rowModel(form: FormGroup): Record<string, unknown> {
    return this.rowData.get(form)?.model ?? {};
  }

  protected rowFields(form: FormGroup): FormlyFieldConfig[] {
    return this.rowData.get(form)?.fields ?? [];
  }

  protected rowOptions(form: FormGroup): FormlyFormOptions {
    return this.rowData.get(form)?.options ?? { formState: this.options.formState };
  }

  /** `formatLabel`/`formatSublabel` are written as `(data, tabIndex) => data?.[tabIndex]?.prop`; a plain object
   *  keyed by the row's own index reproduces that lookup without a real backing array. */
  protected formatLabel(form: FormGroup, index: number): string {
    return this.formatWith(this.props.formatLabel, form, index);
  }

  protected formatSublabel(form: FormGroup, index: number): string {
    return this.formatWith(this.props.formatSublabel, form, index);
  }

  private formatWith(
    formatter: ((data: unknown, index: number) => string) | string | undefined,
    form: FormGroup,
    index: number
  ): string {
    if (!formatter) {
      return "";
    }
    // the live control values, not the stored row model: the nested formly form runs in immutable mode and clones
    // the model, so the stored object never reflects what the user is typing
    const data = { [index]: form.getRawValue() as Record<string, unknown> };
    if (isFunction(formatter)) {
      return formatter(data, index);
    }
    if (isString(formatter)) {
      return this.evaluateStringExpression(formatter, data, index) as string;
    }
    return "";
  }

  private evaluateStringExpression(expression: string, model: unknown, index: number): unknown {
    try {
      const fn = new Function("data", "tabIndex", `return (${expression});`);
      return fn(model, index);
    } catch {
      return undefined;
    }
  }
}
